import {
  AssetType,
  FireTVCampaignAutomationClient,
  PresignedUrlAction,
  SourceAsset,
  SourceAssetMediaType,
  TransformationVariant,
  Placement,
} from '@amzn/fire-tv-campaign-automation-service';
import { KatDropdown, KatFileUpload } from '@amzn/katal-react';
import mime from 'mime-types';
import { ValidationProps } from 'src/components/pages/campaignAutomation/input/assets';
import { ASSET_CONFIG, getValidationProps } from 'src/configs/assetConfig';
import { AssetConstants, ErrorConstants, KatalErrorConstants } from 'src/constants';
import { VALIDATION_CODES } from 'src/constants/validationCodes';
import { AssetUploadError, ValidationError } from 'src/errors/CustomErrors';
import { fetchUrlMetadata } from 'src/helpers';
import { logger } from 'src/logger';
import { metricsPublisher } from 'src/metrics';
import {
  AssetState,
  AssetStatus,
  IAssetInputHandler,
  AssetDetails,
  METRIC_NAME,
  DIMENSION_KEYS,
} from 'src/types';
import { getPresignedUrls } from 'src/utils/clients/controlPlane/apiMethods';
import { secondsToTime } from 'src/utils/DateTimeUtils';
import { getErrorMessage } from 'src/utils/ErrorUtils';
import { formatFileSize } from 'src/utils/fileUtils';
import { AssetMetadataSpecs, DimensionSpecs } from 'src/utils/Types';
import { validateAssetMetadata } from 'src/utils/ValidationUtils';
import { v4 as uuid } from 'uuid';

export interface AssetInputHandlerProps {
  allowedContentTypes: string[];
  assetKeyPrefix: string;
  assetType: AssetType;
  controlPlaneClient: FireTVCampaignAutomationClient;
  setState: (assetState: Partial<AssetState>) => void;
  setAssetDetails: (assetDetails: Partial<AssetDetails>) => void;
  getTransformationVariant: () => TransformationVariant;
  placement: Placement;
  validationProps?: Partial<Record<TransformationVariant, ValidationProps>>;
}

const TIMEOUT_IN_MS = 5000;
// Higher timeout for segments — browsers limit 6 concurrent connections per domain,
// so batched requests queue and their timers tick while waiting for a connection slot.
const SEGMENT_TIMEOUT_IN_MS = 15000;
const FALLBACK_FILE_NAME = 'unnamed';
const SEGMENT_BATCH_SIZE = 30;

/**
 * Abstract base class for handling asset input operations with validation and upload capabilities.
 * Implements the IAssetInputHandler interface to provide consistent asset handling behavior.
 */
export class AssetInputHandler implements IAssetInputHandler {
  protected readonly controlPlaneClient: FireTVCampaignAutomationClient;
  private readonly assetKeyPrefix: string;
  private readonly allowedContentTypes: string[];
  private readonly setState: (assetState: Partial<AssetState>) => void;
  private readonly setAssetDetails: (assetDetails: Partial<AssetDetails>) => void;
  private readonly getTransformationVariant: () => TransformationVariant;
  protected readonly assetType: AssetType;
  private readonly placement: Placement;

  constructor({
    assetKeyPrefix,
    assetType,
    setState,
    setAssetDetails,
    getTransformationVariant,
    controlPlaneClient,
    allowedContentTypes,
    placement,
  }: AssetInputHandlerProps) {
    this.assetKeyPrefix = assetKeyPrefix;
    this.assetType = assetType;
    this.setState = setState;
    this.setAssetDetails = setAssetDetails;
    this.getTransformationVariant = getTransformationVariant;
    this.allowedContentTypes = allowedContentTypes;
    this.controlPlaneClient = controlPlaneClient;
    this.placement = placement;

    this.handleUrl = this.handleUrl.bind(this);
    this.handleFileAttached = this.handleFileAttached.bind(this);
    this.resetAssetState = this.resetAssetState.bind(this);
    this.handleFileReplaced = this.handleFileReplaced.bind(this);
    this.handleFileAttachedError = this.handleFileAttachedError.bind(this);
    this.setInProgress = this.setInProgress.bind(this);
    this.clearError = this.clearError.bind(this);
    this.onTransformationVariantChange = this.onTransformationVariantChange.bind(this);
  }

  /**
   * Clears any error state in the asset handler
   */
  clearError(): void {
    logger.debug('Clearing error');
    this.setState({ error: undefined });
  }

  /**
   * Handles file attachment events from KatFileUpload component
   * @param {KatFileUpload.FilesAttachedEvent} fileAttachedEvent - Event containing attached file information
   * @returns {Promise<void>}
   */
  async handleFileAttached(fileAttachedEvent: KatFileUpload.FilesAttachedEvent): Promise<void> {
    logger.debug('File attached', { fileAttachedEvent });
    this.setInProgress();
    this.setState({ topOffset: undefined, leftOffset: undefined });
    try {
      const file = fileAttachedEvent.detail.files[0];
      await this.handleFileObject(file);
    } catch (error: unknown) {
      const fileName = fileAttachedEvent.detail.files[0]?.name;
      const errorMsg = this.fetchErrorMessage(error, fileName);

      this.setState({
        status: AssetStatus.FAILED,
        error: errorMsg,
        key: undefined,
      });
    }
  }

  onTransformationVariantChange(
    e: KatDropdown.ChangeEvent,
    assetDetails: AssetDetails,
    status: AssetStatus,
  ): void {
    const newValue = e.detail.value as TransformationVariant;
    const isMultiTitle =
      newValue === TransformationVariant.MULTI_TITLE_HORIZONTAL ||
      newValue === TransformationVariant.MULTI_TITLE_VERTICAL;

    this.setState({
      transformationVariant: newValue,
      key: undefined,
      error: undefined,
      // Multi-title variants manage their own image collection via dedicated components —
      // always reset to PENDING_INPUT so the user adds images through MultiImageInput.
      status: isMultiTitle
        ? AssetStatus.PENDING_INPUT
        : assetDetails.key
          ? AssetStatus.PENDING_VALIDATION
          : AssetStatus.PENDING_INPUT,
    });
  }

  /**
   * Handles file attachment error events
   * @param {KatFileUpload.FilesAttachedErrorEvent} fileAttachedErrorEvent - Error event from file attachment
   */
  handleFileAttachedError(fileAttachedErrorEvent: KatFileUpload.FilesAttachedErrorEvent): void {
    logger.error('File attached error', { fileAttachedErrorEvent });
    const errorDetail = fileAttachedErrorEvent.detail;
    const fileInfo = errorDetail.files?.[0]?.file;

    switch (errorDetail.error) {
      case KatalErrorConstants.KATAL_MAX_SIZE_ERROR:
        const MAX_FILE_SIZE_IN_BYTES = getValidationProps(
          this.assetType,
          this.placement,
          this.getTransformationVariant(),
        )?.maxFileSizeInBytes;
        if (!MAX_FILE_SIZE_IN_BYTES) {
          throw new ValidationError(
            `Missing max file size prop for asset type ${this.assetType} with template ${this.getTransformationVariant()}`,
          );
        }
        const maxSizeErrorMessage = `${ErrorConstants.MESSAGES.FAILED_TO_UPLOAD}. Error : Size ${fileInfo?.size ? formatFileSize(fileInfo.size) : 'unknown'} exceeds the maximum file size limit ${formatFileSize(MAX_FILE_SIZE_IN_BYTES)}`;
        logger.error('File size exceeds maximum limit', {
          fileInfo,
          maxSize: MAX_FILE_SIZE_IN_BYTES / 1024,
        });
        this.setState({
          status: AssetStatus.BASIC_VALIDATION_FAILED,
          error: maxSizeErrorMessage,
          key: undefined,
          startTime: undefined,
          endTime: undefined,
        });
        break;

      case KatalErrorConstants.KATAL_UNSUPPORTED_FILE_ERROR:
        const unsupportedTypeErrorMessage = `${ErrorConstants.MESSAGES.FAILED_TO_UPLOAD}. Allowed types: ${this.allowedContentTypes.join(', ')}`;
        logger.error('Unsupported file type', {
          fileInfo,
          allowedTypes: this.allowedContentTypes,
        });
        this.setState({
          status: AssetStatus.BASIC_VALIDATION_FAILED,
          error: unsupportedTypeErrorMessage,
          key: undefined,
          startTime: undefined,
          endTime: undefined,
        });
        break;

      default:
        logger.error(ErrorConstants.MESSAGES.FILE_ATTACHMENT_ERROR, { errorDetail });
        this.setState({
          status: AssetStatus.FAILED,
          error: errorDetail.error,
          key: undefined,
          startTime: undefined,
          endTime: undefined,
        });
        break;
    }
  }

  /**
   * Resets the asset state to its initial values
   */
  resetAssetState(): void {
    logger.debug('Resetting asset state');
    this.setState({
      status: AssetStatus.PENDING_INPUT,
      error: undefined,
      key: undefined,
      logoTitle: undefined,
      logoDescription: undefined,
      cta: undefined,
      providerLogo: undefined,
      startTime: undefined,
      endTime: undefined,
      videoDuration: undefined,
      topOffset: undefined,
      leftOffset: undefined,
    });
    this.setAssetDetails({ key: undefined, file: undefined });
  }

  /**
   * Handles file replacement events
   * @param {KatFileUpload.FilesReplacedEvent} fileReplacedEvent - Event containing new file information
   * @returns {Promise<void>}
   */
  async handleFileReplaced(fileReplacedEvent: KatFileUpload.FilesReplacedEvent): Promise<void> {
    logger.debug('File replaced', { fileReplacedEvent });
    this.setInProgress();
    this.setState({ topOffset: undefined, leftOffset: undefined });
    try {
      const file = fileReplacedEvent.detail.newFiles[0];
      await this.handleFileObject(file);
    } catch (error: unknown) {
      const fileName = fileReplacedEvent.detail.newFiles[0]?.name;
      const errorMsg = this.fetchErrorMessage(error, fileName);

      this.setState({
        status: AssetStatus.FAILED,
        error: errorMsg,
        key: undefined,
      });
    }
  }

  /**
   * Processes a URL input for asset handling
   * @param {string} urlInput - URL to process
   * @returns {Promise<void>}
   */
  async handleUrl(urlInput: string): Promise<void> {
    logger.debug('Handling URL', { urlInput });
    this.setInProgress();
    this.setState({ topOffset: undefined, leftOffset: undefined });
    try {
      await metricsPublisher.trackLatency(
        METRIC_NAME.URL_ASSET_PROCESSING_LATENCY,
        async () => {
          const pathname = new URL(urlInput).pathname.toLowerCase();
          if (pathname.endsWith('.m3u8')) {
            await this.handleM3u8Url(urlInput);
          } else {
            const maxFileSizeInBytes = ASSET_CONFIG[this.assetType].globalMaxFileSizeInBytes;
            const urlMetadata = await fetchUrlMetadata(urlInput, TIMEOUT_IN_MS);
            const specs: AssetMetadataSpecs = {
              maxSize: maxFileSizeInBytes,
              allowedTypes: this.allowedContentTypes,
            };
            validateAssetMetadata(urlMetadata, specs);
            const fileObject = await this.fetchUrlFileObject(urlInput);
            await this.handleFileObject(fileObject);
            metricsPublisher.emitCount(METRIC_NAME.URL_ASSET_PROCESSING_SUCCESS, {
              [DIMENSION_KEYS.ASSET_TYPE]: this.assetType,
            });
          }
        },
        { [DIMENSION_KEYS.ASSET_TYPE]: this.assetType },
      );
    } catch (error: unknown) {
      // Only emit metrics for unexpected system errors, not user validation errors
      if (!(error instanceof ValidationError)) {
        metricsPublisher.emitCount(METRIC_NAME.URL_ASSET_PROCESSING_FAILURE, {
          [DIMENSION_KEYS.ASSET_TYPE]: this.assetType,
        });
      }
      logger.error('URL handling failed', { error, urlInput });
      this.setState({
        status: AssetStatus.BASIC_VALIDATION_FAILED,
        error: getErrorMessage(error),
        key: undefined,
      });
    }
  }

  /**
   * Handles m3u8 playlist URLs by parsing playlists, validating metadata via HEAD requests,
   * downloading the best qualifying variant's segments, stitching them, and uploading to S3.
   */
  private async handleM3u8Url(url: string): Promise<void> {
    logger.info('M3u8 processing started', { url });

    // Fetch master playlist with retry — cold Fargate containers may exceed the 5s timeout
    // on the first attempt due to DNS resolution + TLS handshake to CloudFront.
    let masterResponse: Response;
    try {
      masterResponse = await fetch(url, { signal: AbortSignal.timeout(TIMEOUT_IN_MS) });
    } catch (firstError: unknown) {
      logger.warn('M3u8 master playlist fetch failed, retrying', {
        url,
        error: String(firstError),
      });
      masterResponse = await fetch(url, { signal: AbortSignal.timeout(TIMEOUT_IN_MS) });
    }
    if (!masterResponse.ok) throw new ValidationError('Failed to fetch m3u8 playlist');
    const masterText = await masterResponse.text();

    // Use the user's selected template for validation — same as mp4/mov flow.
    // If the m3u8 doesn't meet the selected template's limits, the user can switch templates.
    const validationProps = getValidationProps(
      this.assetType,
      this.placement,
      this.getTransformationVariant(),
    );
    if (!validationProps?.dimensionSpecification?.minResolution) {
      throw new ValidationError('Missing validation configuration for this asset type');
    }
    const { minResolution, maxResolution } = validationProps.dimensionSpecification;

    // Parse all RESOLUTION variants from the master playlist
    const masterLines = masterText.split('\n');
    const variants: { width: number; height: number; uri: string }[] = [];
    for (let i = 0; i < masterLines.length; i++) {
      const resMatch = masterLines[i].match(/RESOLUTION=(\d+)x(\d+)/);
      if (resMatch) {
        const nextLine = masterLines[i + 1]?.trim();
        if (nextLine && !nextLine.startsWith('#')) {
          variants.push({
            width: parseInt(resMatch[1], 10),
            height: parseInt(resMatch[2], 10),
            uri: nextLine,
          });
        }
      }
    }

    // Filter variants within the acceptable resolution range
    const qualifying = variants.filter(
      (v) =>
        v.width >= minResolution.width &&
        v.height >= minResolution.height &&
        (!maxResolution || (v.width <= maxResolution.width && v.height <= maxResolution.height)),
    );

    if (qualifying.length === 0) {
      const rangeDesc = maxResolution
        ? `between ${minResolution.width}x${minResolution.height} and ${maxResolution.width}x${maxResolution.height}`
        : `at least ${minResolution.width}x${minResolution.height}`;
      throw new ValidationError(`No variant found in playlist with resolution ${rangeDesc}`);
    }

    // Pick the smallest qualifying variant — if it exceeds the size limit,
    // larger variants certainly will too, so no fallback is attempted.
    qualifying.sort((a, b) => a.width * a.height - b.width * b.height);
    const childFilename = qualifying[0].uri;

    // Fetch and parse child playlist — use URL constructor to handle absolute/relative URIs (RFC 8216)
    const childUrl = new URL(childFilename, url).href;
    const childResponse = await fetch(childUrl, { signal: AbortSignal.timeout(TIMEOUT_IN_MS) });
    if (!childResponse.ok) throw new ValidationError('Failed to fetch variant playlist');
    const childText = await childResponse.text();

    // Extract segments and total duration
    const childLines = childText.split('\n');
    const segments: string[] = [];
    let totalDuration = 0;
    for (let i = 0; i < childLines.length; i++) {
      const line = childLines[i].trim();
      if (line.startsWith('#EXTINF:')) {
        const duration = parseFloat(line.substring('#EXTINF:'.length).split(',')[0]);
        if (isNaN(duration)) {
          throw new ValidationError(`Malformed #EXTINF duration at line ${i + 1}`);
        }
        totalDuration += duration;
        const segFile = childLines[i + 1]?.trim();
        if (segFile && !segFile.startsWith('#')) segments.push(segFile);
      }
    }
    if (segments.length === 0) throw new ValidationError('No segments found in playlist');

    logger.info('M3u8 playlist parsed', {
      url,
      variantsFound: variants.length,
      qualifyingVariants: qualifying.length,
      selectedResolution: `${qualifying[0].width}x${qualifying[0].height}`,
      segmentCount: segments.length,
      totalDuration: totalDuration.toFixed(1),
    });

    // Validate duration — prevents full movie uploads and rejects near-empty playlists.
    // Matches the same limits applied to mp4/mov via validateVideoLength() in the browser.
    // Note: duration is derived from #EXTINF metadata, not actual video data.
    if (validationProps?.videoLengthSpecs) {
      const { minDurationInSeconds, maxDurationInSeconds } = validationProps.videoLengthSpecs;
      if (totalDuration < minDurationInSeconds) {
        throw new ValidationError(
          `Video duration ${totalDuration.toFixed(1)}s is outside the allowed range (${minDurationInSeconds}-${maxDurationInSeconds}s)`,
          VALIDATION_CODES.VIDEO_TOO_SHORT,
        );
      }
      if (totalDuration > maxDurationInSeconds) {
        throw new ValidationError(
          `Video duration ${totalDuration.toFixed(1)}s is outside the allowed range (${minDurationInSeconds}-${maxDurationInSeconds}s)`,
          VALIDATION_CODES.VIDEO_TOO_LONG,
        );
      }
    }

    // Get total file size via batched HEAD requests with early rejection
    const maxSize =
      validationProps?.maxFileSizeInBytes ?? ASSET_CONFIG[this.assetType].globalMaxFileSizeInBytes;
    let totalSize = 0;
    for (let i = 0; i < segments.length; i += SEGMENT_BATCH_SIZE) {
      const batch = segments.slice(i, i + SEGMENT_BATCH_SIZE);
      const responses = await Promise.all(
        batch.map((seg) =>
          fetch(new URL(seg, childUrl).href, {
            method: 'HEAD',
            signal: AbortSignal.timeout(SEGMENT_TIMEOUT_IN_MS),
          }),
        ),
      );
      for (const r of responses) {
        const cl = r.headers.get('content-length');
        if (cl) totalSize += parseInt(cl, 10);
      }
      if (totalSize > maxSize) {
        throw new ValidationError(
          `File size ${formatFileSize(totalSize)} exceeds the maximum limit ${formatFileSize(maxSize)}`,
          VALIDATION_CODES.FILE_TOO_LARGE,
        );
      }
    }

    logger.info('M3u8 size validation complete', {
      url,
      totalSize: formatFileSize(totalSize),
      maxSize: formatFileSize(maxSize),
      headBatches: Math.ceil(segments.length / SEGMENT_BATCH_SIZE),
    });

    // Download segments in batches to avoid browser connection/memory limits
    const blobs: Blob[] = [];
    for (let i = 0; i < segments.length; i += SEGMENT_BATCH_SIZE) {
      const batch = segments.slice(i, i + SEGMENT_BATCH_SIZE);
      const batchBlobs = await Promise.all(
        batch.map(async (seg) => {
          const resp = await fetch(new URL(seg, childUrl).href, {
            signal: AbortSignal.timeout(SEGMENT_TIMEOUT_IN_MS),
          });
          if (!resp.ok) throw new ValidationError(`Failed to download segment: ${seg}`);
          return resp.blob();
        }),
      );
      blobs.push(...batchBlobs);
    }
    const stitched = new Blob(blobs, { type: 'video/mp2t' });

    logger.info('M3u8 segments downloaded and stitched', {
      url,
      segmentsDownloaded: blobs.length,
      stitchedSize: formatFileSize(stitched.size),
      downloadBatches: Math.ceil(segments.length / SEGMENT_BATCH_SIZE),
    });

    // Validate MPEG-TS integrity by checking sync bytes (0x47 every 188 bytes).
    // This catches corrupt/misaligned output before uploading — same probe technique ffmpeg uses.
    const probeBytes = new Uint8Array(await blobs[0].slice(0, 188 * 4).arrayBuffer());
    for (let i = 0; i < Math.min(probeBytes.length, 188 * 4); i += 188) {
      if (probeBytes[i] !== 0x47) {
        throw new ValidationError('Invalid MPEG-TS data: missing sync byte');
      }
    }

    const file = new File([stitched], 'video.ts', { type: 'video/mp2t' });

    // Upload to S3, then skip browser-side <video> validation (Chrome cannot play .ts)
    // — duration and resolution were already validated from playlist metadata above
    const { presignedUrl, key } = await this.fetchPresignedUrl(file);
    await this.uploadUsingPresignedUrl(presignedUrl, file);
    this.setAssetDetails({ key, file });
    this.setState({
      status: AssetStatus.BASIC_VALIDATION_SUCCESS,
      key: undefined,
      error: undefined,
      videoDuration: totalDuration,
      startTime: '00:00',
      endTime: secondsToTime(totalDuration),
    });

    logger.info('M3u8 processing complete', {
      url,
      key,
      videoDuration: totalDuration,
      fileSize: formatFileSize(file.size),
    });
  }

  /**
   * Fetches a presigned URL for file upload
   * @param {File} fileObject - File to get presigned URL for
   * @returns {Promise<{key: string, presignedUrl: string}>}
   * @throws {Error} When control plane client is not provided or presigned URL fetch fails
   * @private
   */
  private async fetchPresignedUrl(
    fileObject: File,
  ): Promise<{ key: string; presignedUrl: string }> {
    logger.debug('Fetching presigned URL from control plane', {
      fileObject,
      assetKeyPrefix: this.assetKeyPrefix,
    });

    const key = this.generateS3Key(this.assetKeyPrefix, fileObject);
    try {
      const [presignedUrlResult] = await getPresignedUrls(
        this.controlPlaneClient,
        PresignedUrlAction.PUT,
        [key],
      );

      return presignedUrlResult;
    } catch (error) {
      logger.error('Error occurred while fetching presigned URL', { error });
      throw error;
    }
  }

  /**
   * Fetches a file from a URL and converts it to a File object
   * @param {string} url - URL to fetch file from
   * @returns {Promise<File>}
   * @throws {Error} When fetch fails or content type is missing
   * @private
   */
  private async fetchUrlFileObject(url: string): Promise<File> {
    try {
      const urlObject = new URL(url);
      const response = await fetch(urlObject);
      if (!response.ok) {
        throw new Error(
          `${ErrorConstants.MESSAGES.HTTP_ERROR}: { status: ${response.status} - ${response.statusText} }`,
        );
      }
      const contentType = response.headers.get('content-type');
      if (!contentType) {
        throw new Error('Content type not provided by server');
      }
      const blob = await response.blob();
      const filenameWithExtension = this.getFileNameWithExtension(urlObject.pathname, contentType);
      return new File([blob], filenameWithExtension, {
        type: contentType,
        lastModified: Date.now(),
      });
    } catch (error) {
      const err = new Error(`Failed to fetch file from ${url}: ${getErrorMessage(error)}`);
      err.cause = error;
      throw err;
    }
  }

  /**
   * Generates a unique S3 key for file storage
   * @param {string} keyPrefix - Prefix for the S3 key
   * @param {File} fileObject - File object to generate key for
   * @returns {string} Generated S3 key
   * @private
   */
  private generateS3Key(keyPrefix: string, fileObject: File): string {
    const uniqueId = uuid();
    const originalFilename = fileObject.name;
    let sanitizedName = this.sanitizeFilename(originalFilename);
    const extension = originalFilename.split('.').pop() || '';
    const timestamp = Date.now();
    sanitizedName = sanitizedName.substring(0, AssetConstants.MAX_FILENAME_LENGTH);
    return `${keyPrefix}/${timestamp}_${uniqueId}_${sanitizedName}${extension ? '.' + extension : ''}`;
  }

  /**
   * Generates a filename with appropriate extension based on content type
   * @param {string} pathname - Original pathname
   * @param {string} contentType - MIME type of the content
   * @returns {string} Generated filename with extension
   * @private
   */
  private getFileNameWithExtension(pathname: string, contentType: string): string {
    const fileName = pathname.substring(pathname.lastIndexOf('/') + 1).split('.')[0];
    const extension = mime.extension(contentType) || 'bin';
    return `${fileName}.${extension}`;
  }

  /**
   * Processes a file object through validation and upload
   * @param {File} fileObject - File to process
   * @returns {Promise<void>}
   * @private
   */
  private async handleFileObject(fileObject: File): Promise<void> {
    const { presignedUrl, key } = await this.fetchPresignedUrl(fileObject);
    await this.uploadUsingPresignedUrl(presignedUrl, fileObject);
    this.setAssetDetails({
      key,
      file: fileObject,
    });
    this.setState({
      status: AssetStatus.PENDING_VALIDATION,
      key: undefined,
      error: undefined,
    });
  }

  /**
   * Sanitizes a filename for safe storage
   * @param {string} filename - Original filename
   * @returns {string} Sanitized filename
   * @private
   */
  private sanitizeFilename(filename: string): string {
    try {
      // Handle filenames without extensions properly
      const lastDotIndex = filename.lastIndexOf('.');
      const nameWithoutExtension =
        lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename;

      const encoded = encodeURIComponent(nameWithoutExtension);
      const sanitized = encoded
        .replace(/%20/g, '_')
        .replace(/%[0-9A-Fa-f]{2}/g, '_')
        .replace(/[^a-zA-Z0-9_.-]/g, '_');

      // Ensure non-empty result
      return sanitized || FALLBACK_FILE_NAME;
    } catch (error) {
      logger.error('Error sanitizing filename', { error });
      return FALLBACK_FILE_NAME;
    }
  }

  /**
   * Sets the asset state to in-progress
   * @private
   */
  private setInProgress(): void {
    this.setState({ status: AssetStatus.PENDING_UPLOAD, error: undefined, key: undefined });
  }

  /**
   * Uploads a file using a presigned URL
   * @param {string} presignedUrl - Presigned URL for upload
   * @param {File} fileObject - File to upload
   * @returns {Promise<void>}
   * @throws {Error} When upload fails
   * @private
   */
  private async uploadUsingPresignedUrl(presignedUrl: string, fileObject: File): Promise<void> {
    logger.debug('Uploading file using presigned URL', { presignedUrl });
    await metricsPublisher
      .trackLatency(
        METRIC_NAME.FILE_UPLOAD_LATENCY,
        async () => {
          const response = await fetch(presignedUrl, {
            method: 'PUT',
            body: fileObject,
            headers: {
              'Content-Type': fileObject.type,
            },
          });

          if (!response.ok) {
            throw new Error(
              `${ErrorConstants.MESSAGES.UPLOAD_FAILED_WITH_STATUS}: ${response.status}`,
            );
          }
          logger.debug('File uploaded successfully', { response });
          metricsPublisher.emitCount(METRIC_NAME.FILE_UPLOAD_SUCCESS, {
            [DIMENSION_KEYS.ASSET_TYPE]: this.assetType,
          });
        },
        { [DIMENSION_KEYS.ASSET_TYPE]: this.assetType },
      )
      .catch((error) => {
        logger.error('Error uploading file', { error: getErrorMessage(error) });
        metricsPublisher.emitCount(METRIC_NAME.FILE_UPLOAD_FAILED, {
          [DIMENSION_KEYS.ASSET_TYPE]: this.assetType,
        });
        throw new AssetUploadError(
          `${ErrorConstants.MESSAGES.FAILED_TO_UPLOAD}. Error : ${getErrorMessage(error)}`,
        );
      });
  }

  private fetchErrorMessage(error: unknown, fileName: string | undefined): string {
    if (error instanceof AssetUploadError) return getErrorMessage(error);
    return `${ErrorConstants.MESSAGES.FAILED_TO_UPLOAD}. Error : ${getErrorMessage(error)}`;
  }

  /**
   * Uploads multiple files to S3 and returns their source asset entries.
   * Used by multi-asset variants (e.g., trivia trailer) that bypass the single-file flow.
   */
  async handleMultipleFiles(
    files: File[],
    assetMediaType: SourceAssetMediaType,
  ): Promise<SourceAsset[]> {
    this.setInProgress();
    try {
      const sourceAssets: SourceAsset[] = [];
      for (const file of files) {
        const { presignedUrl, key } = await this.fetchPresignedUrl(file);
        await this.uploadUsingPresignedUrl(presignedUrl, file);
        sourceAssets.push({ sourceAssetKey: key, assetMediaType });
      }
      this.setState({
        status: AssetStatus.PENDING_TRANSFORMATION,
        sourceAssetKeys: sourceAssets,
        error: undefined,
      });
      return sourceAssets;
    } catch (error) {
      const errorMsg = this.fetchErrorMessage(error, undefined);
      this.setState({ status: AssetStatus.FAILED, error: errorMsg, key: undefined });
      throw error;
    }
  }
}
